import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useApolloClient } from '@apollo/client'
import { selectAuth } from '../../services/auth/selectors'
import { selectPrimaryLanguage } from '../../services/user/selectors'
import { parse as parseQuery } from '../../services/query-string/index'
import { historyRedirect, HISTORY_METHOD_REPLACE } from '../../services/navigation/index'
import { URL_AI_CHAT } from '../../services/url/constants'
import { Location, History } from './history-types'
import { GetHistoryDocument } from './queries.generated'

interface UseOracleSessionManagerProps {
  location: Location
  history: History
}

interface SessionManager {
  currentSessionId: string | null
  isNewSession: boolean
  getChatSessionId: () => string | null
  handleSessionReceived: (realSessionId: string, sessionTitle?: string | null) => void
  addChatSessionId: (chatSessionId: string) => void
  clearSessionFromUrl: () => void
}

export const useOracleSessionManager = ({
  location,
  history,
}: UseOracleSessionManagerProps): SessionManager => {
  const auth = useSelector(selectAuth)
  const language = useSelector(selectPrimaryLanguage)
  const apolloClient = useApolloClient()

  // Get current session ID from URL
  const getChatSessionId = useCallback(() => {
    const currentQuery = parseQuery(location.search)
    const urlSessionId = currentQuery.chatSessionId as string
    return urlSessionId || null
  }, [location.search])

  // Check if this is a new session
  const isNewSession = useMemo(() => {
    const currentQuery = parseQuery(location.search)
    const urlSessionId = currentQuery.chatSessionId as string
    return !urlSessionId
  }, [location.search])

  // Current session ID
  const currentSessionId = getChatSessionId()

  // Add session ID to URL. Always lands on the bare chat home — a chat
  // session is a chat-page concern, never a divination-entry one (AI-821).
  const addChatSessionId = useCallback((chatSessionId: string) => {
    const currentQuery = parseQuery(location.search)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { prompt, feature, ...queryWithoutOneShot } = currentQuery

    historyRedirect({
      history,
      url: URL_AI_CHAT,
      query: { ...queryWithoutOneShot, chatSessionId },
      auth,
      language,
      historyMethod: HISTORY_METHOD_REPLACE,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any)
  }, [location.search, history, auth, language])

  // Clear session from URL (for new chats). Normalizes back to chat home.
  const clearSessionFromUrl = useCallback(() => {
    const currentQuery = parseQuery(location.search)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { chatSessionId, ...everythingElse } = currentQuery

    historyRedirect({
      history,
      url: URL_AI_CHAT,
      query: everythingElse,
      auth,
      language,
      historyMethod: HISTORY_METHOD_REPLACE,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any)
  }, [location.search, history, auth, language])

  // Handle when real session ID is received from server
  const handleSessionReceived = useCallback(
    (realSessionId: string, sessionTitle?: string | null) => {
      const urlSessionId = parseQuery(location.search).chatSessionId as string

      // Only update URL if we're currently in a new session (no URL ID)
      if (!urlSessionId) {
        addChatSessionId(realSessionId)

        // Add the real session to cache for immediate sidebar update
        const realSession = {
          __typename: 'OracleChatSession',
          id: realSessionId,
          name: sessionTitle || 'New Chat',
          personalityId: null,
          favorite: false,
          favoriteUpdatedAt: null,
          updatedAt: new Date().toISOString(),
          contentId: null,
          featureMediaId: null,
        }

        // Add the session to the non-favorites query specifically
        try {
          const existingQuery = apolloClient.cache.readQuery({
            query: GetHistoryDocument,
            variables: {
              limit: 100,
              offset: 0,
              favorite: false,
              order: [['activityUpdatedAt', 'DESC']],
            },
          }) as { oracleChatSessionList?: { oracleChatSessions: Array<{ id: string }> } }

          if (existingQuery?.oracleChatSessionList) {
            // Check if session already exists
            const existingSession = existingQuery.oracleChatSessionList.oracleChatSessions.find(
              (session: { id: string }) => session.id === realSessionId,
            )

            if (!existingSession) {
              // Write the updated query with the new session
              apolloClient.cache.writeQuery({
                query: GetHistoryDocument,
                variables: {
                  limit: 100,
                  offset: 0,
                  favorite: false,
                  order: [['activityUpdatedAt', 'DESC']],
                },
                data: {
                  oracleChatSessionList: {
                    ...existingQuery.oracleChatSessionList,
                    oracleChatSessions: [
                      realSession,
                      ...existingQuery.oracleChatSessionList.oracleChatSessions,
                    ],
                  },
                },
              })
            }
          }
        } catch (error) {
          // If reading the query fails, the session will appear after the next refetch
          // This is a fallback to ensure we don't break the app
          // eslint-disable-next-line no-console
          console.warn('Could not update cache for new session:', error)
        }

        // Note: We don't need to refetch here anymore as the sidebar will handle
        // its own smart refetch when needed. The cache update above should be sufficient
        // to show the new conversation in the list.
      }
    },
    [location.search, addChatSessionId, apolloClient],
  )

  return {
    currentSessionId,
    isNewSession,
    getChatSessionId,
    handleSessionReceived,
    addChatSessionId,
    clearSessionFromUrl,
  }
}
